/**
 * Turns a rejected scenario mutation into a user-facing message.
 * @module features/squad-planning/utils/resolveScenarioErrorMessage
 */

/** Shown when the failure carries no usable message. */
export const SCENARIO_ERROR_FALLBACK =
  "Something went wrong saving your scenario. Your changes are still here — please try again.";

/** Minimal shape of the GraphQL error list Apollo attaches to its errors. */
interface GraphQLErrorLike {
  readonly message?: unknown;
}

/**
 * Reads the first non-empty message from a list of GraphQL errors.
 * @param candidate - A value that may be a GraphQL error array
 * @returns The first non-empty message, or null
 */
function firstMessageOf(candidate: unknown): string | null {
  if (!Array.isArray(candidate)) return null;
  for (const entry of candidate as readonly GraphQLErrorLike[]) {
    const message = entry?.message;
    if (typeof message === "string" && message.trim().length > 0) {
      return message.trim();
    }
  }
  return null;
}

/**
 * Reads the first GraphQL error message off an Apollo-style error.
 *
 * Checks `graphQLErrors` first (a 200 response carrying errors), then
 * `networkError.result.errors`. The second path matters because Apollo Client 3
 * classifies any non-2xx response as a `ServerError`: the GraphQL errors the
 * backend sent are then reachable only through `networkError.result`, while the
 * top-level `message` degrades to "Response not successful: Received status
 * code 400". Reading only the top level would show that instead of the real
 * validation or role-guard message.
 * @param error - The rejected value
 * @returns The first non-empty GraphQL message, or null
 */
function readGraphQLMessage(error: object): string | null {
  const direct = firstMessageOf(
    (error as { graphQLErrors?: unknown }).graphQLErrors
  );
  if (direct != null) return direct;

  const networkError = (error as { networkError?: unknown }).networkError;
  if (typeof networkError !== "object" || networkError === null) return null;

  const result = (networkError as { result?: unknown }).result;
  if (typeof result !== "object" || result === null) return null;

  return firstMessageOf((result as { errors?: unknown }).errors);
}

/**
 * Resolves a human-readable message from an unknown rejection value.
 *
 * Prefers the first GraphQL error message (backend validation and role guards
 * report there, whether the response was a 200 carrying errors or a non-2xx
 * wrapped as a network error), then the Error message, then a generic fallback
 * that reassures the user their draft is intact.
 * @param error - The value a scenario mutation rejected with
 * @returns A non-empty message safe to render in the UI
 */
export function resolveScenarioErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const graphQLMessage = readGraphQLMessage(error);
    if (graphQLMessage != null) return graphQLMessage;

    const { message } = error as { message?: unknown };
    if (typeof message === "string" && message.trim().length > 0) {
      return message.trim();
    }
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error.trim();
  }

  return SCENARIO_ERROR_FALLBACK;
}
